import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { V1_PROGRAMS_QUERY_KEY, V1GetPrograms } from '@/features/programs/api'
import type { ProgramType, V1PeriodType, V1ProgramType, V2PeriodType, V2ProgramType } from '@/features/programs/types'
import { PROGRAM_TYPES } from '@/features/programs/types'

const toProgramType = (value: string | undefined): ProgramType | undefined =>
  PROGRAM_TYPES.find((type) => type === value)

function getLatestPeriod({ periods }: { periods: V1PeriodType[] }): V2PeriodType | undefined {
  const latestPeriod = periods[0]
  if (latestPeriod) {
    return {
      id: latestPeriod.id,
      name: latestPeriod.name,
      startAt: latestPeriod.startAt,
      endAt: latestPeriod.endAt,
    }
  }
  return undefined
}

function V1ToV2DataMiddleware({ data }: { data?: V1ProgramType[] }): V2ProgramType[] {
  const programs = data?.map((program) => {
    const programSettings = {
      moderationPiiEnabled:
        program.settings.find((setting) => setting.name === 'MODERATION_PII_ENABLED')?.value === 'true',
      mentorMatchingCriteria: program.settings.find((setting) => setting.name === 'MATCHING_MENTOR_CRITERIA')?.value,
      menteeMatchingCriteria: program.settings.find((setting) => setting.name === 'MATCHING_MENTEE_CRITERIA')?.value,
      matchingCriteriaEnabled:
        program.settings.find((setting) => setting.name === 'MATCHING_CRITERIA_ENABLED')?.value === 'true',
      matchingAutoSuggestEnabled:
        program.settings.find((setting) => setting.name === 'MATCHING_AUTO_SUGGEST_ENABLED')?.value === 'true',
      matchingAdditionalInstructions: program.settings.find(
        (setting) => setting.name === 'MATCHING_ADDITIONAL_INSTRUCTIONS',
      )?.value,
      maxUserPerGroup: Number(program.settings.find((setting) => setting.name === 'MAX_USER_PER_GROUP')?.value),
      maxGroupPerMentee: Number(program.settings.find((setting) => setting.name === 'MAX_GROUP_PER_MENTEE')?.value),
      maxGroupPerMentor: Number(program.settings.find((setting) => setting.name === 'MAX_GROUP_PER_MENTOR')?.value),
      hasMentorReviewSessionDeliverableReport:
        program.settings.find((setting) => setting.name === 'MENTOR_REVIEW_SESSION_DELIVERABLE_REPORT_STEP')?.value ===
        'true',
      hasMentorReviewSessionDeliverableRate:
        program.settings.find((setting) => setting.name === 'MENTOR_REVIEW_SESSION_DELIVERABLE_RATE_STEP')?.value ===
        'true',
      hasMentorReviewSessionPresence:
        program.settings.find((setting) => setting.name === 'MENTOR_REVIEW_SESSION_PRESENCE_STEP')?.value === 'true',
      hasReviewSessionRating:
        program.settings.find((setting) => setting.name === 'REVIEW_SESSION_RATING_STEP')?.value === 'true',
      hasReviewSessionReview:
        program.settings.find((setting) => setting.name === 'REVIEW_SESSION_REVIEW_STEP')?.value === 'true',
      hasReviewSessionDeliverable:
        program.settings.find((setting) => setting.name === 'REVIEW_SESSION_DELIVERABLE_STEP')?.value === 'true',
      adminDashboardType: program.settings.find((setting) => setting.name === 'ADMIN_DASHBOARD_TYPE')?.value,
      programType: toProgramType(program.settings.find((setting) => setting.name === 'PROGRAM_TYPE')?.value),
      mentorReportTemplate: program.settings.find((setting) => setting.name === 'MENTOR_REPORT_TEMPLATE')?.value,
      hasMenteeReviewSessionReport:
        program.settings.find((setting) => setting.name === 'MENTEE_REVIEW_SESSION_REPORT_STEP')?.value === 'true',
      menteeReportTemplate: program.settings.find((setting) => setting.name === 'MENTEE_REPORT_TEMPLATE')?.value,
      i18nMenteeName: program.settings.find((setting) => setting.name === 'I18N_MENTEE_NAME')?.value,
      i18nMentorName: program.settings.find((setting) => setting.name === 'I18N_MENTOR_NAME')?.value,
      menteeCanBookSession:
        program.settings.find((setting) => setting.name === 'MENTEE_CAN_BOOK_SESSION')?.value === 'true',
      defaultInactivityDays: Number(
        program.settings.find((setting) => setting.name === 'DEFAULT_INACTIVITY_DAYS')?.value,
      ),
      shouldMentorReviewSessionDeliverableReport:
        program.settings.find((setting) => setting.name === 'MENTOR_REVIEW_SESSION_DELIVERABLE_REPORT_STEP')?.value ===
        'true',
      shouldMenteeReviewSessionReport:
        program.settings.find((setting) => setting.name === 'MENTEE_REVIEW_SESSION_REPORT_STEP')?.value === 'true',
      shouldMentorReviewSessionDeliverableRate:
        program.settings.find((setting) => setting.name === 'MENTOR_REVIEW_SESSION_DELIVERABLE_RATE_STEP')?.value ===
        'true',
      shouldMentorReviewSessionPresence:
        program.settings.find((setting) => setting.name === 'MENTOR_REVIEW_SESSION_PRESENCE_STEP')?.value === 'true',
      shouldReviewSessionRating:
        program.settings.find((setting) => setting.name === 'REVIEW_SESSION_RATING_STEP')?.value === 'true',
      shouldReviewSessionDeliverable:
        program.settings.find((setting) => setting.name === 'REVIEW_SESSION_DELIVERABLE_STEP')?.value === 'true',
      shouldReviewSessionReview:
        program.settings.find((setting) => setting.name === 'REVIEW_SESSION_REVIEW_STEP')?.value === 'true',
      logo: program.settings.find((setting) => setting.name === 'LOGO')?.value,
      logoSmall: program.settings.find((setting) => setting.name === 'LOGO_SMALL')?.value,
      favicon: program.settings.find((setting) => setting.name === 'FAVICON')?.value,
      colorPrimary: program.settings.find((setting) => setting.name === 'COLOR_PRIMARY')?.value,
      colorSecondary: program.settings.find((setting) => setting.name === 'COLOR_SECONDARY')?.value,
    }

    return {
      id: program.id,
      name: program.name,
      description: program.description,
      color: program.color ?? null,
      latestPeriod: getLatestPeriod({ periods: program.periods }),
      periods: program.periods,
      settings: programSettings,
      users: program.users?.map((user) => ({ id: user.userId })) || [],
    }
  })

  return programs || []
}

const usePrograms = () => {
  const { accountRef } = useAccount()

  const userIdRef = computed(() => accountRef.value?.id)
  const isProgramsQueryEnabledRef = computed(() => !!userIdRef.value)

  const query = useQuery({
    queryKey: [V1_PROGRAMS_QUERY_KEY],
    queryFn: () => V1GetPrograms(userIdRef.value!),
    enabled: isProgramsQueryEnabledRef,
  })

  const programsDataRef = computed(() =>
    V1ToV2DataMiddleware({
      data: query.data.value,
    }),
  )

  return {
    programsRef: programsDataRef,
    isPendingRef: query.isPending,
    isFetchedRef: query.isFetched,
  }
}

export default usePrograms
